import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Quick WebAudio motif player (oscillator fallback).
// Replace with your piano samples when ready.
const NOTE_FREQ = {
  "C5": 523.25, "C#5": 554.37, "D5": 587.33, "D#5": 622.25, "E5": 659.25,
  "B4": 493.88,
};
const MOTIF = ["E5","D#5","E5","D#5","E5","B4","D5","C5"];

function playMotif({ tempoBpm = 240 } = {}) {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const beat = 60 / tempoBpm; // seconds per beat
  let t = ctx.currentTime + 0.1;

  MOTIF.forEach((n, i) => {
    const f = NOTE_FREQ[n];
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = f;
    osc.type = "sine";
    gain.gain.value = 0.0001;
    osc.connect(gain).connect(ctx.destination);
    const dur = (i < 5 ? 0.33 : 0.50) * beat; // slightly longer at the end
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.linearRampToValueAtTime(0.12, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.start(t);
    osc.stop(t + dur + 0.02);
    t += dur * 1.05;
  });
}

export default function GisRevealModal({ open, onClose }) {
  const [showStaff, setShowStaff] = useState(false);

  useEffect(() => {
    if (open) {
      setShowStaff(false);
      // 1) Small delay, then draw staff lines
      const a = setTimeout(() => setShowStaff(true), 600);
      // 2) Play motif after lines appear
      const b = setTimeout(() => playMotif({ tempoBpm: 260 }), 1000);
      return () => { clearTimeout(a); clearTimeout(b); };
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 bg-black/70 z-[999] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-3xl rounded-2xl overflow-hidden bg-gradient-to-b from-slate-900 to-slate-800 shadow-2xl"
            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.98, opacity: 0 }}
          >
            {/* Background image reused for continuity */}
            <img src="/images/constellation_bg.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />

            <div className="relative p-6 md:p-8">
              <h2 className="text-white text-2xl md:text-3xl font-extrabold mb-2">Ancient Melody Discovered!</h2>
              <p className="text-white/90 mb-4">Gis the Star Gazer deciphers the constellation.</p>

              {/* Staff lines reveal */}
              <div className="relative w-full aspect-[3/1] rounded-xl overflow-hidden bg-transparent">
                {/* staff via SVG */}
                <svg viewBox="0 0 1200 400" className="absolute inset-0 w-full h-full">
                  {[0,1,2,3,4].map(i => (
                    <motion.line key={i}
                      x1={60} x2={1140} y1={80 + i*60} y2={80 + i*60}
                      stroke="rgba(255,255,255,0.95)" strokeWidth="4"
                      initial={{ pathLength: 0 }} animate={{ pathLength: showStaff ? 1 : 0 }}
                      transition={{ duration: 0.6, delay: 0.1 * i }}
                    />
                  ))}
                </svg>
              </div>

              <div className="mt-4 flex gap-3 justify-end">
                <button
                  className="px-4 py-2 rounded-lg bg-white text-slate-900 font-semibold shadow"
                  onClick={onClose}
                >
                  Continue
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-yellow-400/90 text-slate-900 font-bold shadow"
                  onClick={() => playMotif({ tempoBpm: 260 })}
                >
                  Play Motif Again
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
